import { Box } from '@mui/material';
import type { TFunction } from 'i18next';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { InlineAlert } from '../../../core-components/theme/components/InlineAlert';
import { emptyRegionSx } from '../../../core-components/theme/components/Screen';
import type { CodeProblem, CodeStatus } from '../domain/codeStatus';
import type { LoginCodeSession } from '../hooks/logic/loginMachine';

import { AuthPanelHeading, authContentSx, authPanelSx } from './AuthPanel';
import { CodeChallengeStep } from './CodeChallengeStep';
import { changeTargetLabel, verifiesText } from './identifierText';

const HALTED: readonly CodeStatus['kind'][] = ['expired', 'blocked'];

const notesOf = (t: TFunction, session: LoginCodeSession, status: CodeStatus): readonly string[] => {
  if (!session.sent.verifiesIdentifier || HALTED.includes(status.kind)) return [];
  return [verifiesText(t, session.sent.sentOn)];
};

const WaitingNotice: React.FC<{ readonly waiting: boolean }> = ({ waiting }) => {
  const { t } = useTranslation();
  return (
    <Box role="status" sx={emptyRegionSx}>
      {waiting && <InlineAlert severity="info" message={t('common.waitingForConnection', 'Waiting for the connection to come back…')} />}
    </Box>
  );
};

export interface LoginCodePanelProps {
  readonly session: LoginCodeSession;
  readonly status: CodeStatus;
  readonly resending: boolean;
  readonly resendProblem: CodeProblem | null;
  readonly waiting: boolean;
  readonly onCodeChange: (value: string) => void;
  readonly onComplete: (value: string) => void;
  readonly onResend: () => void;
  readonly onChangeIdentifier: () => void;
}

export const LoginCodePanel: React.FC<LoginCodePanelProps> = ({
  session,
  status,
  resending,
  resendProblem,
  waiting,
  onCodeChange,
  onComplete,
  onResend,
  onChangeIdentifier,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const { sent } = session;

  return (
    <Box sx={authPanelSx}>
      <Box sx={authContentSx}>
        <AuthPanelHeading title={t('passcodeAuth.code.title', 'Enter the code')} titleId={titleId} />
        <CodeChallengeStep
          key={sent.sentOn}
          channel={sent.sentOn}
          target={sent.maskedTo}
          notes={notesOf(t, session, status)}
          labelledBy={titleId}
          length={sent.codeLength}
          code={session.code}
          attempt={session.attempt}
          status={status}
          resendAt={session.resendAt}
          resending={resending}
          resendProblem={resendProblem}
          changeLabel={changeTargetLabel(t, sent.sentOn)}
          notice={<WaitingNotice waiting={waiting} />}
          onCodeChange={onCodeChange}
          onComplete={onComplete}
          onResend={onResend}
          onChangeTarget={onChangeIdentifier}
        />
      </Box>
    </Box>
  );
};
